import React from 'react'
import { ModuleNames } from '../../../../constants/componentNames.constants'
import ImgUtil from '../../../../utils/img.utils'
import { isExternalURL } from '../../../../utils/link.utils'
import formatInlineCode from '../../../../utils/shortTextFormatting'
import Button from '../../button'
import MainNavBanner from '../../mainNavBanner'
import Typography from '../../typography'
import WrapperImage from '../../wrapperImage'
import {
  MegaMenu,
  MegaMenuContainer,
  MegaMenuSection,
  NavLink,
  NavLinksWrapper,
  SubListItem,
} from '../style'

const handleAccordionOpen = (event: any) => {
  const current = event.currentTarget
  const parent = event.currentTarget.parentElement

  if (current.classList.contains('is-active')) {
    current.classList.remove('is-active')
    parent.classList.remove('NavLink__Active')
  } else {
    let menuItems = document.querySelectorAll('.js-Menu-item')

    for (let item of menuItems) {
      if (item !== event.currentTarget) {
        const itemParent = item.parentElement
        itemParent?.classList.remove('NavLink__Active')
        item.classList.remove('is-active')
      }
    }

    parent.classList.add('NavLink__Active')
    current.classList.add('is-active')
  }
}

export const NavLinks = ({ navLinks, openMenu, uiPathSrc }: any) => (
  <NavLinksWrapper openMenu={openMenu}>
    {navLinks?.map(
      ({ id, newGroupName, parentLink, pages, greyColumns, greyColumnAlignment }: any) => {
        const internalLinkSrc =
          parentLink?.internalLink?.slug && uiPathSrc + parentLink.internalLink.slug
        const menuGroupSrc = parentLink?.externalUrl || internalLinkSrc || '#'
        const menuGroupTarget = isExternalURL(menuGroupSrc) ? '_blank' : '_self'

        return (
          <NavLink key={id}>
            <button
              className="Navbar__Mobile_Button Menu-item js-Menu-item"
              onClick={e => handleAccordionOpen(e)}>
              <Typography type="header6" className="Navbar__Link_Name">
                {newGroupName}
              </Typography>
            </button>

            <div className="Navbar__MenuGroup">
              <a href={menuGroupSrc} target={menuGroupTarget}>
                <Typography type="header6" className="Navbar__Link_Name">
                  {newGroupName}
                </Typography>
              </a>

              <NavMegaMenu
                subLinks={pages}
                numOfGrayColumns={Number(greyColumns)}
                direction={greyColumnAlignment}
                uiPathSrc={uiPathSrc}
                navLinkId={id}
              />
            </div>
          </NavLink>
        )
      }
    )}
  </NavLinksWrapper>
)

const NavMegaMenu = ({ subLinks, numOfGrayColumns, direction, uiPathSrc, navLinkId }: any) => {
  let mainNavBannerData: any = null
  if (subLinks[subLinks.length - 1]?.__typename === ModuleNames.MAIN_NAV_BANNER) {
    mainNavBannerData = subLinks[subLinks.length - 1]
  }

  return (
    <MegaMenu
      className="Navbar__MegaMenu"
      id={`MegaMenu_${navLinkId}`}
      direction={direction}
      numOfGrayColumns={numOfGrayColumns}
      maxLength={subLinks?.length}
      doesMainNavBannerExist={!!mainNavBannerData}>
      <div className="MegaMenu__Side"></div>
      <MegaMenuContainer>
        {subLinks?.map(
          (
            {
              groupNameColor,
              menuGroupDescription,
              newGroupName,
              pages,
              id,
              highlightedLinks,
              __typename,
            }: any,
            idx: number
          ) => {
            const hasIconLink = pages && pages[0]?.__typename === ModuleNames.TOPIC_ICON_LINK
            highlightedLinks?.forEach((highlightedLink: any) => {
              highlightedLink.isHighlightedLink = true
            })

            const displaySublistComponent = () => {
              const pagesWithHighlightedLinks = pages ? pages.concat(highlightedLinks || []) : []
              const mid = Math.ceil(pagesWithHighlightedLinks.length / 2) || 0
              const leftPages = pagesWithHighlightedLinks.slice(0, mid)
              const rightPages = pagesWithHighlightedLinks.slice(mid)

              const sectionName = (
                <Typography type="label" className="Navbar__Section_Name">
                  {newGroupName}
                </Typography>
              )

              const sectionDescription = (
                <Typography type="caption" className="Navbar__Section_Description">
                  {formatInlineCode(menuGroupDescription)}
                </Typography>
              )

              switch (__typename) {
                case ModuleNames.MENU_GROUP:
                  return (
                    <div className="Navbar__Section_Container">
                      {(newGroupName || menuGroupDescription) && (
                        <div className="Navbar__Section_Content">
                          {newGroupName && sectionName}
                          {menuGroupDescription && sectionDescription}
                        </div>
                      )}

                      {(leftPages.length || rightPages.length) && (
                        <>
                          <div className="SubList__Left">{SubList(leftPages, uiPathSrc)}</div>

                          <div className="SubList__Right">{SubList(rightPages, uiPathSrc)}</div>
                        </>
                      )}
                    </div>
                  )

                case ModuleNames.MAIN_NAV_BANNER:
                  return <MainNavBanner mainNavBannerData={mainNavBannerData} navType="New Nav" />

                default:
                  return null
              }
            }

            return (
              <MegaMenuSection
                key={id}
                textColor={groupNameColor}
                numOfGrayColumns={numOfGrayColumns}
                position={idx + 1}
                direction={direction}
                maxLength={subLinks?.length}
                doesContentExist={menuGroupDescription || newGroupName}
                hasIconLink={hasIconLink}
                doesMainNavBannerExist={!!mainNavBannerData}>
                {displaySublistComponent()}
              </MegaMenuSection>
            )
          }
        )}
      </MegaMenuContainer>
      <div className="MegaMenu__Side"></div>
    </MegaMenu>
  )
}

const SubList = (subList: any, uiPathSrc: string) =>
  subList?.map(
    ({
      externalUrl,
      id,
      internalLink,
      text,
      ctaText,
      icon,
      url,
      __typename,
      description,
      isHighlightedLink,
    }: any) => {
      const internalLinkSrc = internalLink?.slug && uiPathSrc + internalLink.slug
      const subListUrl = externalUrl || internalLinkSrc || '#'
      const subListTarget = isExternalURL(subListUrl) ? '_blank' : '_self'
      const iconLinkTarget = isExternalURL(url) ? '_blank' : '_self'
      const iconUrl = icon?.image?.file?.url || null

      const iconImg = (
        <WrapperImage
          src={ImgUtil.getResizedImageUrl(icon?.image?.file?.url, '')}
          width={18}
          alt={icon?.altText}
        />
      )

      const iconLabel = <Typography type="label">{ctaText}</Typography>

      const iconDescription = (
        <Typography type="caption" className="Navbar__IconLink_Caption">
          {description}
        </Typography>
      )

      return (
        <SubListItem key={id} subListLength={subList.length} isThereDescription={description}>
          {__typename === ModuleNames.TOPIC_ICON_LINK ? (
            <a href={url} target={iconLinkTarget} className="Navbar__SubList_IconLink">
              <div className="Navbar__IconLink_Header">
                {iconUrl && iconImg}
                {ctaText && iconLabel}
              </div>
              {description && iconDescription}
            </a>
          ) : isHighlightedLink ? (
            <Button
              className="Navbar__HighlightedLink"
              icon={true}
              type={'text-link'}
              theme={'light'}
              href={subListUrl}
              text={text}
              size="medium"
              arrowOrientation="right"
            />
          ) : (
            <a href={subListUrl} target={subListTarget} className="Navbar__SubList_Cta">
              <Typography type="label">{text}</Typography>
            </a>
          )}
        </SubListItem>
      )
    }
  )
